import React, { useState } from 'react';
import { Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from '../hooks/use-toast';
import { timeSlots } from '../mock';
import { contactAPI, bookingsAPI } from '../utils/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    date: undefined,
    time: ''
  });

  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await contactAPI.submit(formData);
      toast({
        title: "Message Sent",
        description: response.data.message,
      });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      // Error handling - consider using error logging service in production
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formattedDate = bookingData.date.toISOString().split('T')[0];
      const bookingPayload = {
        ...bookingData,
        date: formattedDate
      };
      
      const response = await bookingsAPI.create(bookingPayload);
      toast({
        title: "Consultation Booked",
        description: response.data.message,
      });
      setBookingData({ name: '', email: '', phone: '', projectType: '', date: undefined, time: '' });
    } catch (error) {
      // Error handling - consider using error logging service in production
      const errorMessage = error.response?.data?.detail || "Failed to book consultation. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-8">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl leading-relaxed">
            Have a project in mind? Let's discuss how we can bring your architectural vision to life.
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-16 px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 mb-20">
            <div>
              <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Email</h3>
              <a href="mailto:hello@archstudio.com" className="text-2xl text-gray-900 hover:text-gray-600 transition-colors">
                hello@archstudio.com
              </a>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-3">Office</h3>
              <p className="text-xl text-gray-900">123 Architecture Lane</p>
              <p className="text-xl text-gray-900">New York, NY 10001</p>
            </div>
          </div>

          {/* Forms */}
          <div className="max-w-3xl">
            <Tabs defaultValue="contact" className="w-full">
              <TabsList className="mb-12 bg-transparent border-b border-gray-200 rounded-none p-0 h-auto">
                <TabsTrigger 
                  value="contact"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 px-0 pb-4 mr-8 text-lg"
                >
                  Send Message
                </TabsTrigger>
                <TabsTrigger 
                  value="booking"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-gray-900 data-[state=active]:bg-transparent data-[state=active]:text-gray-900 px-0 pb-4 text-lg"
                >
                  Book Consultation
                </TabsTrigger>
              </TabsList>

              {/* Contact Form */}
              <TabsContent value="contact">
                <form onSubmit={handleContactSubmit} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <Label htmlFor="contact-name" className="text-sm uppercase tracking-wider text-gray-500">Name *</Label>
                      <Input
                        id="contact-name"
                        className="border-gray-300 rounded-none h-12 text-base"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="contact-email" className="text-sm uppercase tracking-wider text-gray-500">Email *</Label>
                      <Input
                        id="contact-email"
                        type="email"
                        className="border-gray-300 rounded-none h-12 text-base"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="contact-subject" className="text-sm uppercase tracking-wider text-gray-500">Subject *</Label>
                    <Input
                      id="contact-subject"
                      className="border-gray-300 rounded-none h-12 text-base"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-3">
                    <Label htmlFor="contact-message" className="text-sm uppercase tracking-wider text-gray-500">Message *</Label>
                    <Textarea
                      id="contact-message"
                      className="border-gray-300 rounded-none min-h-40 text-base"
                      rows={8}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="bg-gray-900 hover:bg-gray-800 text-white rounded-none h-12 px-8 text-base"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </TabsContent>

              {/* Booking Form */}
              <TabsContent value="booking">
                <form onSubmit={handleBookingSubmit} className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <Label htmlFor="booking-name" className="text-sm uppercase tracking-wider text-gray-500">Name *</Label>
                      <Input
                        id="booking-name"
                        className="border-gray-300 rounded-none h-12 text-base"
                        value={bookingData.name}
                        onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="booking-email" className="text-sm uppercase tracking-wider text-gray-500">Email *</Label>
                      <Input
                        id="booking-email"
                        type="email"
                        className="border-gray-300 rounded-none h-12 text-base"
                        value={bookingData.email}
                        onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <Label htmlFor="booking-phone" className="text-sm uppercase tracking-wider text-gray-500">Phone *</Label>
                      <Input
                        id="booking-phone"
                        type="tel"
                        className="border-gray-300 rounded-none h-12 text-base"
                        value={bookingData.phone}
                        onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="booking-type" className="text-sm uppercase tracking-wider text-gray-500">Project Type *</Label>
                      <Select
                        value={bookingData.projectType}
                        onValueChange={(value) => setBookingData({ ...bookingData, projectType: value })}
                        required
                      >
                        <SelectTrigger id="booking-type" className="border-gray-300 rounded-none h-12 text-base">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="commercial">Commercial</SelectItem>
                          <SelectItem value="corporate">Corporate</SelectItem>
                          <SelectItem value="institutional">Institutional</SelectItem>
                          <SelectItem value="transportation">Transportation</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                      <Label className="text-sm uppercase tracking-wider text-gray-500">Select Date *</Label>
                      <Calendar
                        mode="single"
                        selected={bookingData.date}
                        onSelect={(date) => setBookingData({ ...bookingData, date })}
                        disabled={(date) => date < new Date()}
                        className="border border-gray-300"
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="booking-time" className="text-sm uppercase tracking-wider text-gray-500">Select Time *</Label>
                      <Select
                        value={bookingData.time}
                        onValueChange={(value) => setBookingData({ ...bookingData, time: value })}
                        required
                      >
                        <SelectTrigger id="booking-time" className="border-gray-300 rounded-none h-12 text-base">
                          <SelectValue placeholder="Select time" />
                        </SelectTrigger>
                        <SelectContent>
                          {timeSlots.map((slot) => (
                            <SelectItem key={slot} value={slot}>
                              {slot}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="bg-gray-900 hover:bg-gray-800 text-white rounded-none h-12 px-8 text-base"
                    disabled={!bookingData.date || !bookingData.time || submitting}
                  >
                    {submitting ? 'Booking...' : 'Book Consultation'}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;